/**
 * API Client for innovanet portal
 * 
 * Axios-based API client with automatic token handling,
 * error handling, and request/response interceptors.
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';

// API Configuration
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.innovanet.com';

/** Optional getter for current tenant code; set by app so tenant-scoped requests send X-Tenant-Code. */
let tenantCodeGetter: (() => string | null) | null = null;
export function setTenantCodeGetter(getter: () => string | null): void {
  tenantCodeGetter = getter;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true, // Important for session cookies
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // If data is FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Add language header from localStorage
    const lang = typeof window !== 'undefined' 
      ? localStorage.getItem('language') || 'sk' 
      : 'sk';
    config.headers['Accept-Language'] = lang;
    
    // Add CSRF token if available
    const csrfToken = typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      : null;
    if (csrfToken) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
    }

    const tenantCode = tenantCodeGetter?.() ?? null;
    if (tenantCode) {
      config.headers['X-Tenant-Code'] = tenantCode;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Optional minimum loading delays (first response 2s, second 4s) for UX – only when enabled
const API_LOADING_DELAYS_ENABLED = typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_LOADING_DELAYS === 'true';
const API_LOADING_DELAYS_MS = [2000, 4000] as const; // first time 2s, second time 4s; no longer (user satisfaction)
let apiLoadingDelayCount = 0;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Response interceptor
api.interceptors.response.use(
  async (response) => {
    if (API_LOADING_DELAYS_ENABLED && apiLoadingDelayCount < API_LOADING_DELAYS_MS.length) {
      const waitMs = API_LOADING_DELAYS_MS[apiLoadingDelayCount];
      apiLoadingDelayCount += 1;
      await delay(waitMs);
    }
    return response;
  },
  (error: AxiosError) => {
    // Handle specific error codes
    if (error.response) {
      const status = error.response.status;
      const requestUrl = error.config?.url || '';
      
      // Rate limiting - don't redirect, just log
      if (status === 429) {
        console.warn('Rate limited - too many requests');
        // Don't redirect on rate limiting
        return Promise.reject(error);
      }
      
      // Unauthorized - redirect to login (but not for auth check endpoints)
      if (status === 401) {
        // Don't auto-redirect for auth check/user endpoints - let the app handle it
        const isAuthCheckEndpoint = requestUrl.includes('/api/auth/check') ||
                                    requestUrl.includes('/api/auth/user') ||
                                    requestUrl.includes('/api/general-info') ||
                                    requestUrl.includes('/api/master/') ||
                                    requestUrl.includes('/api/tenant/') ||
                                    requestUrl.includes('/api/impersonate/');
        
        if (typeof window !== 'undefined' &&
            !window.location.pathname.includes('/login') &&
            !isAuthCheckEndpoint) {
          window.location.href = '/login?session=expired';
        }
      }
      
      // Forbidden - show permission error
      if (status === 403) {
        console.error('Permission denied');
      }
      
      // Not found
      if (status === 404) {
        console.error('Resource not found');
      }
      
      // Server error
      if (status >= 500) {
        console.error('Server error');
      }
    }
    
    return Promise.reject(error);
  }
);

// API Response types
/** Payload from GET /api/general-info (dashboard bootstrap) */
export interface GeneralInfoPayload {
  user: Record<string, unknown> | null;
  tenant: { id?: number; code?: string; database?: string } | null;
  tenants?: unknown[];
  needsTenantCreation?: boolean;
  settings?: Record<string, string>;
  permissions?: { id: number; app: string; level: number; fromGroup?: number }[];
  licenses?: unknown[];
  links?: { id: number; name: string; url: string; order: number }[];
  sidebar?: PageSidebarDto[];
  modules?: AppModule[];
  formatting?: Record<string, unknown> | null;
  pending_count?: number;
  tasks?: unknown[];
  news?: unknown[];
  news_comment_counts?: Record<number, number>;
  events?: unknown[];
  vehicle_events?: { id: number; vehicle_id: number; vehicle_ecv: string; event_type_id: number; event_type_name: string; event_date: string }[];
  restaurants?: unknown[];
  restaurant_cities?: { id: number; name: string; order: number }[];
  account_units?: { unit_id: number; unit_name: string; [k: string]: unknown }[];
  tenant_users?: { Id: number; FirstName: string; LastName: string; UserName?: string; email?: string; name?: string; Photo?: string | null; LastRefresh?: string | null }[];
  positions?: { id: number; name: Record<string, string> | string }[];
  financial_stats_available_months?: { months?: { year: number; month: number }[] };
  financial_stats?: { income?: number; outcome?: number; profit?: number; income_prev_month?: number; outcome_prev_month?: number; profit_prev_month?: number; top_customers?: { name: string; total: number }[] } | null;
  financial_stats_yearly?: { months?: { month: number; income?: number; outcome?: number; profit?: number }[] } | null;
  servicedesk?: { items: unknown[]; total: number; page: number; limit: number; total_pages: number };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  // Auth-specific fields
  authenticated?: boolean;
  require2FA?: boolean;
  require2FASetup?: boolean;
  requireTenantSelection?: boolean;
  userId?: number;
  qrCodeUrl?: string;
  secret?: string;
  tenants?: TenantInfo[];
  tenant?: TenantInfo;
  user?: UserInfo;
  settings?: Record<string, string>;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
  redirectUrl?: string;
}

export interface NewsItemDto {
  id: number;
  userId: number;
  name: Record<string, string>;
  content: Record<string, string>;
  photo?: string | null;
  likes: number[];
  likesCount: number;
  createDate: string;
  author?: {
    id: number;
    firstName?: string;
    lastName?: string;
    userName?: string;
    photo?: string | null;
  } | null;
}

export interface NewsCommentDto {
  id: number;
  newsId: number;
  userId: number;
  comment: string;
  createDate: string;
  author?: {
    id: number;
    firstName?: string;
    lastName?: string;
    userName?: string;
    photo?: string | null;
  } | null;
}

// User info from API
export interface UserInfo {
  id: number;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
  FirstName?: string;
  LastName?: string;
  UserName?: string;
  eMail?: string;
  photoUrl?: string;
  position?: string;
  orgUnit?: string;
}

// Auth API
export const authApi = {
  login: (userName: string, password: string) =>
    api.post<ApiResponse>('/api/auth/login', { userName, password }),
    
  verify2FA: (userId: number, token: string) =>
    api.post<ApiResponse>('/api/auth/2fa', { userId, token }),
    
  logout: () =>
    api.post<ApiResponse>('/api/auth/logout'),
    
  getUser: () =>
    api.get<ApiResponse>('/api/auth/user'),
    
  check: () =>
    api.get<ApiResponse>('/api/auth/check'),

  /** Notify backend that user opened a module (sends ntfy push, throttled). */
  moduleOpened: (module: string, path?: string) =>
    api.post<ApiResponse>('/api/auth/module-opened', { module, path }),

  /** Single bootstrap: user, licenses, links, sidebar, modules, formatting, pending_count, news, events, restaurants, account_units, tenant_users, positions. Pass tenantCode to send X-Tenant-Code (e.g. after login before dashboard mounts). */
  getGeneralInfo: (tenantCode?: string) =>
    api.get<ApiResponse<GeneralInfoPayload>>('/api/general-info', tenantCode ? { headers: { 'X-Tenant-Code': tenantCode } } : undefined),
    
  changePassword: (currentPassword: string, newPassword: string, newPassword_confirmation: string) =>
    api.post<ApiResponse>('/api/auth/change-password', { 
      currentPassword, 
      newPassword, 
      newPassword_confirmation 
    }),
    
  refresh: () =>
    api.post<ApiResponse>('/api/auth/refresh'),
    
  // OAuth
  getProviders: () =>
    api.get<ApiResponse>('/api/auth/providers'),
    
  googleRedirect: () =>
    api.get<ApiResponse>('/api/auth/google/redirect'),
    
  microsoftRedirect: () =>
    api.get<ApiResponse>('/api/auth/microsoft/redirect'),
    
  linkGoogle: () =>
    api.post<ApiResponse>('/api/auth/google/link'),
    
  unlinkGoogle: () =>
    api.post<ApiResponse>('/api/auth/google/unlink'),
    
  linkMicrosoft: () =>
    api.post<ApiResponse>('/api/auth/microsoft/link'),
    
  unlinkMicrosoft: () =>
    api.post<ApiResponse>('/api/auth/microsoft/unlink'),
    
  getLinkedAccounts: () =>
    api.get<ApiResponse>('/api/auth/linked-accounts'),
    
  // Tenant switching
  switchTenant: (tenantCode: string, userId: number) =>
    api.post<ApiResponse>('/api/auth/switch-tenant', {
      tenant_code: tenantCode,
      user_id: userId
    }),
    
  // Get available tenants for current user
  getTenants: () =>
    api.get<ApiResponse>('/api/auth/tenants'),
    
  // Forgot password
  forgotPassword: (email: string) =>
    api.post<ApiResponse>('/api/auth/forgot-password', { email }),
    
  resetPassword: (email: string, token: string, password: string, password_confirmation: string) =>
    api.post<ApiResponse>('/api/auth/reset-password', { email, token, password, password_confirmation }),
    
  // Registration
  register: (data: { firstName: string; lastName: string; email: string; phone?: string; password: string; password_confirmation: string }) =>
    api.post<ApiResponse>('/api/auth/register', data),
    
  verifyEmail: (email: string, token: string) =>
    api.get<ApiResponse>('/api/auth/verify-email', { params: { email, token } }),
    
  resendVerification: (email: string) =>
    api.post<ApiResponse>('/api/auth/resend-verification', { email }),
    
  // Complete invitation (for invited users)
  completeInvitation: (data: {
    token: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    password_confirmation: string;
    phone?: string;
    tenant?: string;
  }) =>
    api.post<ApiResponse>('/api/auth/complete-invitation', data),
    
  // Language preference (user saved; requires auth)
  getLanguage: () =>
    api.get<ApiResponse & { language?: string }>('/api/auth/language'),
    
  updateLanguage: (language: string) =>
    api.post<ApiResponse & { language?: string }>('/api/auth/language', { language }),

  // WebAuthn (biometric) – requires authenticated session
  getWebAuthnRegisterOptions: () =>
    api.get<ApiResponse & { options?: Record<string, unknown> }>('/api/auth/webauthn/register/options'),
  registerWebAuthn: (credential: Record<string, unknown>, deviceName?: string) =>
    api.post<ApiResponse>('/api/auth/webauthn/register', { credential, deviceName }),
  getWebAuthnCredentials: () =>
    api.get<ApiResponse<{ id: number; deviceName: string; createdAt: string; lastUsedAt: string | null }[]>>('/api/auth/webauthn/credentials'),
  deleteWebAuthnCredential: (id: number) =>
    api.delete<ApiResponse>(`/api/auth/webauthn/credentials/${id}`),
};

// Impersonation API (@innovanet.com only)
export const impersonationApi = {
  getTenants: () =>
    api.get<ApiResponse<{ id: number; code: string; database?: string; name?: string }[]>>('/api/impersonate/tenants'),
  getTenantUsers: (tenantId: number) =>
    api.get<ApiResponse<{ id: number; email: string; firstName: string; lastName: string; name: string }[]>>(`/api/impersonate/tenants/${tenantId}/users`),
  start: (tenantId: number, userId: number) =>
    api.post<ApiResponse>('/api/impersonate/start', { tenant_id: tenantId, user_id: userId }),
  end: () =>
    api.post<ApiResponse>('/api/impersonate/end'),
  status: () =>
    api.get<ApiResponse<{
      active: boolean;
      originalUser?: { masterUserId: number; userId: number; userName: string; tenantCode: string | null };
      impersonatedUser?: { userId: number; masterUserId: number; userName: string };
      impersonatedTenant?: { code: string; database: string };
    }>>('/api/impersonate/status'),
};

// i18n / Language service API (no auth; current locale + available languages)
export interface LocaleResponse {
  success: boolean;
  data?: {
    language: string;
    languageCode: string;
    availableLanguages: { name: string; code: string }[];
  };
}
export const languageApi = {
  getLocale: () =>
    api.get<LocaleResponse>('/api/language/locale'),
};

// Users API
export const usersApi = {
  list: (params?: { page?: number; per_page?: number; search?: string; active?: number }) =>
    api.get<ApiResponse>('/api/users', { params }),
    
  get: (id: number) =>
    api.get<ApiResponse>(`/api/users/${id}`),
    
  create: (data: any) =>
    api.post<ApiResponse>('/api/users', data),
    
  update: (id: number, data: any) =>
    api.put<ApiResponse>(`/api/users/${id}`, data),
    
  delete: (id: number) =>
    api.delete<ApiResponse>(`/api/users/${id}`),
    
  getPermissions: (id: number) =>
    api.get<ApiResponse>(`/api/users/${id}/permissions`),

  /** PUT /api/users/{id}/permissions – set direct permissions (replaces all direct; FromGroup=0). */
  setPermissions: (id: number, permissions: { app: string; level: number; fromGroup?: number }[]) =>
    api.put<ApiResponse>(`/api/users/${id}/permissions`, { permissions }),
    
  activate: (id: number, active: boolean) =>
    api.post<ApiResponse>(`/api/users/${id}/activate`, { active }),
    
  select: (params?: { search?: string }) =>
    api.get<ApiResponse>('/api/users/select', { params }),
    
  byOrgUnit: (orgUnitId: number) =>
    api.get<ApiResponse>(`/api/users/by-org-unit/${orgUnitId}`),
    
  byPosition: (positionId: number) =>
    api.get<ApiResponse>(`/api/users/by-position/${positionId}`),
    
  // Tenant user management
  /** GET /api/users/tenant - list for dropdowns. Id is MASTER user ID (master.users.Id). Use this Id as user_id when creating/updating internal organization person. */
  tenant: () => api.get<ApiResponse<{ Id: number; FirstName: string; LastName: string; UserName?: string; email?: string }[]>>('/api/users/tenant'),
  tenantList: (params?: { page?: number; per_page?: number; search?: string; active?: number }) =>
    api.get<ApiResponse>('/api/users/tenant-list', { params }),
    
  addToTenant: (email: string) =>
    api.post<ApiResponse>('/api/users/add-to-tenant', { email }),

  /** POST /api/users/create-attendance-only – create a "Povoliť iba dochádzku" user (no email required). If email is provided, an initialization email is sent. */
  createAttendanceOnly: (data: { FirstName: string; LastName: string; eMail?: string }) =>
    api.post<ApiResponse>('/api/users/create-attendance-only', data),

  /** PATCH /api/users/{id}/update-profile – update email (master.users), Type (tenant.users), and FirstName/LastName for non-activated users only. */
  updateProfile: (id: number, data: { eMail?: string; Type?: string | null; FirstName?: string; LastName?: string }) =>
    api.patch<ApiResponse>(`/api/users/${id}/update-profile`, data),

  /** GET /api/users/hr-users – returns users with profile level 1 or organization level 1 permission, used for post-save notifications. */
  getHrUsers: () =>
    api.get<ApiResponse<{ id: number; name: string }[]>>('/api/users/hr-users'),

  /** POST /api/users/{id}/resend-invitation – resend invitation email (tenant context, invited users only). */
  resendInvitation: (id: number) =>
    api.post<ApiResponse>(`/api/users/${id}/resend-invitation`),

  /** PUT /api/users/{id}/tenant-role – set role (admin|user) in current tenant. */
  setTenantRole: (id: number, role: 'admin' | 'user') =>
    api.put<ApiResponse>(`/api/users/${id}/tenant-role`, { role }),
    
  toggleTenantStatus: (id: number) =>
    api.post<ApiResponse>(`/api/users/${id}/toggle-tenant-status`),
};

/** System admin: tenant user management (no tenant context; use tenantId). */
export const systemTenantUsersApi = {
  /** Master users not in this tenant (for "add existing user"). */
  getAvailable: (tenantId: number, search?: string) =>
    api.get<ApiResponse<{ id: number; email: string; firstName: string; lastName: string; fullName: string }[]>>(
      `/api/system/tenants/${tenantId}/users/available`,
      search ? { params: { search } } : undefined
    ),
  addUser: (tenantId: number, email: string) =>
    api.post<ApiResponse>(`/api/system/tenants/${tenantId}/users/add`, { email }),
  resendInvitation: (tenantId: number, userId: number) =>
    api.post<ApiResponse>(`/api/system/tenants/${tenantId}/users/${userId}/resend-invitation`),
  updateRole: (tenantId: number, userId: number, role: 'admin' | 'user') =>
    api.put<ApiResponse>(`/api/system/tenants/${tenantId}/users/${userId}/role`, { role }),
  /** Toggle user active status in tenant (enable/disable). */
  toggleStatus: (tenantId: number, userId: number) =>
    api.patch<ApiResponse>(`/api/system/tenants/${tenantId}/users/${userId}/toggle-status`),
  /** Remove user from tenant. */
  removeUser: (tenantId: number, userId: number) =>
    api.delete<ApiResponse>(`/api/system/tenants/${tenantId}/users/${userId}`),
};

// Tasks API
export const tasksApi = {
  list: (params?: {
    userId?: number;
    activeOnly?: boolean;
    page?: number;
    per_page?: number;
    status?: number;
    dateFrom?: string;
    dateTo?: string;
    app?: string;
    fromUserId?: number;
  }) =>
    api.get<ApiResponse>('/api/tasks', { params }),
  get: (id: number) =>
    api.get<ApiResponse>(`/api/tasks/${id}`),
    
  create: (data: any) =>
    api.post<ApiResponse>('/api/tasks', data),

  /** Create notification-only task (no approve/decline; e.g. "Bol pridaný komentár k stránke") */
  createNotification: (data: {
    to: number;
    app: string;
    task: string;
    description?: string;
    parentId?: number;
    submenu?: string;
    variable?: string;
    from?: number;
  }) =>
    api.post<ApiResponse<{ id: number }>>('/api/tasks/notification', data),
    
  update: (id: number, data: any) =>
    api.put<ApiResponse>(`/api/tasks/${id}`, data),
    
  delete: (id: number) =>
    api.delete<ApiResponse>(`/api/tasks/${id}`),
    
  complete: (id: number, reason?: string) =>
    api.post<ApiResponse>(`/api/tasks/${id}/complete`, { reason }),
    
  decline: (id: number, reason: string) =>
    api.post<ApiResponse>(`/api/tasks/${id}/decline`, { reason }),
  
  /** "Zamietnuť s vrátením" – decline and create return task N steps back in one action (pending tasks). */
  declineWithReturn: (id: number, reason: string, stepsBack?: number) =>
    api.post<ApiResponse<{ declinedId: number; returnTaskId: number }>>(`/api/tasks/${id}/decline-with-return`, { reason, stepsBack: stepsBack ?? 1 }),
  
  cancel: (id: number, reason?: string) =>
    api.post<ApiResponse>(`/api/tasks/${id}/cancel`, { reason }),
  
  /** Return an already-declined task N steps back (so someone earlier in the chain can fix). */
  returnTask: (id: number, stepsBack?: number, reason?: string) =>
    api.post<ApiResponse<{ id: number }>>(`/api/tasks/${id}/return`, { stepsBack: stepsBack ?? 1, reason }),
  
  /** For pending or declined task, get how many steps back are available (for "Zamietnuť s vrátením" UI). */
  getReturnOptions: (id: number) =>
    api.get<ApiResponse<{ maxStepsBack: number; steps: { stepsBack: number; userId: number; taskId: number }[] }>>(`/api/tasks/${id}/return-options`),
  
  /** Batch approve (complete) multiple tasks. */
  batchApprove: (taskIds: number[], reason?: string) =>
    api.post<ApiResponse<{ completed: number; failed: number; completedIds: number[]; errors: Record<number, string> }>>('/api/tasks/batch-approve', { taskIds, reason }),
  
  reassign: (id: number, toUserId: number, reason?: string) =>
    api.post<ApiResponse>(`/api/tasks/${id}/reassign`, { userId: toUserId, reason }),
    
  getStatus: (params: { app: string; parentId?: number; submenu?: string; type?: string }) =>
    api.get<ApiResponse>('/api/tasks/status', { params }),
    
  getPendingCount: () =>
    api.get<ApiResponse>('/api/tasks/pending-count'),
    
  byApp: (app: string) =>
    api.get<ApiResponse>(`/api/tasks/by-app/${app}`),
    
  byParent: (parentId: number, params?: { app?: string }) =>
    api.get<ApiResponse>(`/api/tasks/by-parent/${parentId}`, { params }),

  /** Workflow for any module: workflowStatus, workflowLabel, tasks (enriched), activeTaskId. */
  getWorkflow: (parentId: number, params: { app: string; submenu?: string }) =>
    api.get<ApiResponse<TaskWorkflowDto>>(`/api/tasks/workflow/${parentId}`, { params }),
};

/** Response of GET /api/tasks/workflow/{parentId}?app= */
export interface TaskWorkflowDto {
  workflowStatus: 'pending' | 'in_progress' | 'approved' | 'declined';
  workflowLabel: string;
  /** sequential = 1 by 1; parallel = all approvers at once */
  workflowApprovalMode?: 'sequential' | 'parallel';
  tasks: TaskWorkflowTaskDto[];
  activeTaskId: number | null;
  /** When parallel: all pending task ids for current user */
  activeTaskIds?: number[];
}

export interface TaskWorkflowTaskDto {
  id: number;
  task: string;
  description: string | null;
  toUserId: number;
  toUserName: string | null;
  toUserPhoto: string | null;
  fromUserId: number | null;
  fromUserName: string | null;
  status: number;
  statusLabel: string;
  active: number;
  dateCreated: string;
  dateDone: string | null;
  reason: string | null;
}

// Search API
export interface GlobalSearchResult {
  id: number;
  display: string;
  data: Record<string, unknown>;
  route: string;
}

export interface GlobalSearchModuleResult {
  items: GlobalSearchResult[];
  count: number;
  icon: string;
  route: string;
}

export interface GlobalSearchResponse {
  success: boolean;
  data: Record<string, GlobalSearchModuleResult>;
  totalCount: number;
  query: string;
}

export interface SearchCategoriesResponse {
  success: boolean;
  data: {
    categories: string[];
    modules: string[];
  };
}

export const searchApi = {
  search: (q: string, params?: { tables?: string[]; mode?: string }) =>
    api.get<ApiResponse>('/api/search', { params: { q, ...params } }),
    
  searchUsers: (q: string) =>
    api.get<ApiResponse>('/api/search/users', { params: { q } }),
    
  searchCompanies: (q: string) =>
    api.get<ApiResponse>('/api/search/companies', { params: { q } }),
    
  searchTasks: (q: string) =>
    api.get<ApiResponse>('/api/search/tasks', { params: { q } }),
    
  globalSearch: (q: string, category?: string, limit?: number) =>
    api.get<GlobalSearchResponse>('/api/search/global', { 
      params: { 
        q, 
        ...(category && category !== 'all' ? { category } : {}),
        ...(limit ? { limit } : {})
      } 
    }),
    
  quickSearch: (q: string) =>
    api.get<ApiResponse>('/api/search/quick', { params: { q } }),
    
  getCategories: () =>
    api.get<SearchCategoriesResponse>('/api/search/categories'),
    
  getMode: () =>
    api.get<ApiResponse>('/api/search/mode'),
    
  setMode: (mode: 'fulltext' | 'fast') =>
    api.post<ApiResponse>('/api/search/mode', { mode }),
};

// Logs API
export const logsApi = {
  list: (params?: { app?: string; parentId?: number; userId?: number; limit?: number }) =>
    api.get<ApiResponse>('/api/logs', { params }),
    
  get: (id: number) =>
    api.get<ApiResponse>(`/api/logs/${id}`),
    
  create: (data: { app: string; action: string; description?: string; parentId?: number }) =>
    api.post<ApiResponse>('/api/logs', data),
    
  getStats: () =>
    api.get<ApiResponse>('/api/logs/stats'),
    
  byDateRange: (params: { from: string; to: string; app?: string }) =>
    api.get<ApiResponse>('/api/logs/by-date', { params }),
    
  byUser: (userId: number) =>
    api.get<ApiResponse>(`/api/logs/user/${userId}`),
    
  byApp: (app: string) =>
    api.get<ApiResponse>(`/api/logs/app/${app}`),
    
  print: (module: string, parentId: number) =>
    api.get<ApiResponse>(`/api/logs/print/${module}/${parentId}`),
};

// News API
export const newsApi = {
  list: (params?: { limit?: number; offset?: number }) =>
    api.get<ApiResponse<NewsItemDto[]>>('/api/news', { params }),
  get: (id: number) =>
    api.get<ApiResponse<NewsItemDto>>(`/api/news/${id}`),
  create: (data: { name: Record<string, string> | string; content: Record<string, string>; photo?: string | null }) =>
    api.post<ApiResponse<{ id: number }>>('/api/news', data),
  update: (id: number, data: { name: Record<string, string> | string; content: Record<string, string>; photo?: string | null }) =>
    api.put<ApiResponse>(`/api/news/${id}`, data),
  remove: (id: number) =>
    api.delete<ApiResponse>(`/api/news/${id}`),
  like: (id: number) =>
    api.post<ApiResponse<{ likes: number[] }>>(`/api/news/${id}/like`),
  unlike: (id: number) =>
    api.post<ApiResponse<{ likes: number[] }>>(`/api/news/${id}/unlike`),
  listComments: (id: number) =>
    api.get<ApiResponse<NewsCommentDto[]>>(`/api/news/${id}/comments`),
  addComment: (id: number, comment: string) =>
    api.post<ApiResponse<{ id: number }>>(`/api/news/${id}/comments`, { comment }),
  deleteComment: (newsId: number, commentId: number) =>
    api.delete<ApiResponse>(`/api/news/${newsId}/comments/${commentId}`),
};

// Events API (calendar/timeline)
export interface EventDto {
  id: number;
  name: string;
  place: string | null;
  dateFrom: string;
  dateTo: string;
  assumed: boolean;
  note: string | null;
  userId: number | null;
  createdAt: string;
  updatedAt: string | null;
  author?: {
    id: number;
    firstName: string;
    lastName: string;
    userName: string;
    photo: string | null;
  } | null;
}

export const eventsApi = {
  list: (params?: { dateFrom?: string; dateTo?: string; limit?: number; offset?: number }) =>
    api.get<ApiResponse<EventDto[]>>('/api/events', { params }),
  get: (id: number) =>
    api.get<ApiResponse<EventDto>>(`/api/events/${id}`),
  create: (data: {
    name: string;
    place?: string | null;
    dateFrom: string;
    dateTo: string;
    assumed?: boolean;
    note?: string | null;
  }) =>
    api.post<ApiResponse<{ id: number }>>('/api/events', data),
  update: (id: number, data: {
    name: string;
    place?: string | null;
    dateFrom: string;
    dateTo: string;
    assumed?: boolean;
    note?: string | null;
  }) =>
    api.put<ApiResponse>(`/api/events/${id}`, data),
  remove: (id: number) =>
    api.delete<ApiResponse>(`/api/events/${id}`),
};

// Data storage – folders (tree), files per folder, versions, log
export interface DataStorageFolderNode {
  id: number;
  name: string;
  parent_id: number | null;
  children: DataStorageFolderNode[];
}

export interface DataStorageUploadedByUser {
  id: number;
  name: string;
  photo: string | null;
}

export interface DataStorageFileItem {
  id: number;
  name: string;
  storage_path: string;
  size: number;
  mime_type: string | null;
  uploaded_by: number | null;
  created_at: string;
  updated_at: string | null;
  modified_by?: string;
  size_formatted?: string;
  uploaded_by_user?: DataStorageUploadedByUser | null;
}

export interface DataStorageInitialResponse {
  folders: DataStorageFolderNode[];
  default_folder_id: number | null;
  files: DataStorageFileItem[];
}

export interface DataStorageFileLogEntry {
  id: number;
  file_id: number;
  action: string;
  user_id: number | null;
  created_at: string;
  extra: Record<string, unknown> | null;
  user_name?: string;
}

export interface DataStorageFileVersion {
  id: number;
  file_id: number;
  version_number: number;
  storage_path: string;
  size: number;
  uploaded_by: number | null;
  created_at: string;
  uploaded_by_name?: string;
  size_formatted?: string;
  uploaded_by_user?: DataStorageUploadedByUser | null;
}

export const dataStorageApi = {
  /** Single call: folders tree + files (optional folder_id for which folder's files). */
  getInitial: (folderId?: number | null) =>
    api.get<ApiResponse<DataStorageInitialResponse>>(
      '/api/data-storage',
      folderId != null ? { params: { folder_id: folderId } } : undefined
    ),
  listFolders: () =>
    api.get<ApiResponse<DataStorageFolderNode[]>>('/api/data-storage/folders'),
  createFolder: (data: { parent_id?: number | null; name: string }) =>
    api.post<ApiResponse<DataStorageFolderNode>>('/api/data-storage/folders', data),
  updateFolder: (id: number, data: { name: string }) =>
    api.put<ApiResponse<DataStorageFolderNode>>(`/api/data-storage/folders/${id}`, data),
  deleteFolder: (id: number) =>
    api.delete<ApiResponse>(`/api/data-storage/folders/${id}`),
  listFiles: (folderId: number) =>
    api.get<ApiResponse<DataStorageFileItem[]>>(`/api/data-storage/folders/${folderId}/files`),
  uploadFile: (folderId: number, form: FormData) =>
    api.post<ApiResponse<DataStorageFileItem>>(`/api/data-storage/folders/${folderId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  createVersion: (fileId: number, form: FormData) =>
    api.post<ApiResponse<DataStorageFileItem>>(`/api/data-storage/files/${fileId}/version`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFile: (fileId: number) =>
    api.delete<ApiResponse>(`/api/data-storage/files/${fileId}`),
  getFileLog: (fileId: number) =>
    api.get<ApiResponse<DataStorageFileLogEntry[]>>(`/api/data-storage/files/${fileId}/log`),
  getFileVersions: (fileId: number) =>
    api.get<ApiResponse<DataStorageFileVersion[]>>(`/api/data-storage/files/${fileId}/versions`),
  downloadFile: (fileId: number) =>
    api.get<Blob>(`/api/data-storage/files/${fileId}/download`, { responseType: 'blob' }),
  /** Stiahnuť starú verziu (prílohu) podľa čísla verzie. */
  downloadVersion: (fileId: number, versionNumber: number) =>
    api.get<Blob>(`/api/data-storage/files/${fileId}/versions/${versionNumber}/download`, { responseType: 'blob' }),
};

// Business trips (Služobné cesty)
export interface BusinessTripDto {
  id: number;
  user_id: number;
  name: string;
  type: 1 | 2;
  date_from: string;
  date_to: string;
  currency: string;
  cash_advance: number;
  transfer: string | null;
  car_id: string | null;
  peoples: string | null;
  document_nr: string | null;
  /** Live workflow status from TaskService (-1 no tasks, 1 pending, 999 done, 997 declined, 998 canceled). */
  workflow_status: number;
  created_at: string;
  updated_at: string;
}

export interface BusinessTripPointDto {
  id: number;
  trip_id: number;
  point_type: 1 | 2;
  date_from: string | null;
  date_to: string | null;
  country: string | null;
  place_start: string | null;
  place_end: string | null;
  place: string | null;
  purpose: string | null;
  travel: number;
  accommodation: number;
  accommodation_card: number;
  other: number;
  other_card: number;
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
  ignore_allday: boolean;
}

export const businessTripsApi = {
  getSettings: () =>
    api.get<ApiResponse<{ accountant_id: number | null; allow_cash_advance: boolean; default_country_iso?: string }>>('/api/business-trips/settings'),
  updateSettings: (data: { accountant_id?: number | null; allow_cash_advance?: boolean; default_country_iso?: string | null }) =>
    api.put<ApiResponse>('/api/business-trips/settings', data),
  countries: () =>
    api.get<ApiResponse<{ countries: { isocode: string; country: string }[] }>>('/api/business-trips/countries'),
  list: (params?: { mine_only?: boolean; status?: number; date_from?: string; date_to?: string }) =>
    api.get<ApiResponse<{ trips: BusinessTripDto[] }>>('/api/business-trips', { params }),
  get: (id: number) =>
    api.get<ApiResponse<{
      trip: BusinessTripDto;
      points: BusinessTripPointDto[];
      billing_summary: { days: Record<string, { amount: number; currency: string; sk_hours: number; abroad_hours: number; breakfast: boolean; lunch: boolean; dinner: boolean }>; totals: { meals: number; travel: number; accommodation: number; other: number; total: number }; currency: string; billing_points: BusinessTripPointDto[] };
      tasks: { Id: number; Task: string; To: number; From: number; Status: number; Active: number; DateDone: string | null; Reason: string | null; Description: string | null }[];
      changelog: { type: string; action?: string; task?: string; description?: string; user_id: number; date: string; reason?: string }[];
      requires_attachment_if_payments: boolean;
    }>>(`/api/business-trips/${id}`),
  create: (data: { name: string; type: 1 | 2; date_from: string; date_to: string; currency: string; cash_advance?: number; transfer?: string; car_id?: string; peoples?: string }) =>
    api.post<ApiResponse<{ trip_id: number }>>('/api/business-trips', data),
  update: (id: number, data: { name?: string; document_nr?: string; car_id?: string; peoples?: string }) =>
    api.put<ApiResponse>(`/api/business-trips/${id}`, data),
  cancel: (id: number) =>
    api.post<ApiResponse>(`/api/business-trips/${id}/cancel`),
  currencies: () =>
    api.get<ApiResponse<{ currencies: string[] }>>('/api/business-trips/currencies'),
  getPoints: (id: number, type?: 1 | 2) =>
    api.get<ApiResponse<{ points: BusinessTripPointDto[] }>>(`/api/business-trips/${id}/points`, type != null ? { params: { type } } : undefined),
  addPoint: (id: number, pointType: 1 | 2, data: Record<string, unknown>) =>
    api.post<ApiResponse<{ point_id: number }>>(`/api/business-trips/${id}/points`, { ...data, point_type: pointType }),
  updatePoint: (id: number, pointId: number, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/api/business-trips/${id}/points/${pointId}`, data),
  deletePoint: (id: number, pointId: number) =>
    api.delete<ApiResponse>(`/api/business-trips/${id}/points/${pointId}`),
  setMealFlag: (id: number, pointId: number, meal: 'breakfast' | 'lunch' | 'dinner', value: boolean) =>
    api.post<ApiResponse>(`/api/business-trips/${id}/points/${pointId}/meal`, { meal, value }),
  billingSummary: (id: number) =>
    api.get<ApiResponse<{ days: Record<string, unknown>; totals: Record<string, number>; currency: string }>>(`/api/business-trips/${id}/billing-summary`),
  changelog: (id: number) =>
    api.get<ApiResponse<{ changelog: { type: string; action?: string; task?: string; description?: string; user_id: number; date: string }[] }>>(`/api/business-trips/${id}/changelog`),
  completeTask: (taskId: number, status: number, reason?: string) =>
    api.post<ApiResponse>(`/api/business-trips/tasks/${taskId}/complete`, { status, reason }),
  listAttachments: (id: number) =>
    api.get<ApiResponse<{ attachments: { id: number; path: string; original_name: string | null; added_by: number; created_at: string }[] }>>(`/api/business-trips/${id}/attachments`),
  uploadAttachment: (id: number, form: FormData) =>
    api.post<ApiResponse>(`/api/business-trips/${id}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadAttachment: (id: number, attachmentId: number) =>
    api.get<Blob>(`/api/business-trips/${id}/attachments/download?attachment_id=${attachmentId}`, { responseType: 'blob' }),
  deleteAttachment: (id: number, attachmentId: number) =>
    api.delete<ApiResponse>(`/api/business-trips/${id}/attachments`, { data: { attachment_id: attachmentId } }),
};

// Assets API (Správa majetku)
export interface AssetCategoryDto {
  id: number;
  name: string;
  sortOrder: number;
  /** JSON array of user ids: [responsible, manager?, ...] */
  responsiblePersonIds?: number[] | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface AssetDto {
  id: number;
  name: string;
  serialNumber: string | null;
  categoryId: number | null;
  categoryName: string | null;
  purchasedAt: string | null;
  price: number | null;
  currencyId: number | null;
  currencyCode: string | null;
  supplier: string | null;
  ownerUserId: number | null;
  ownerLabel: string | null;
  ownerName: string | null;
  ownerPhoto: string | null;
  warrantyUntil: string | null;
  customFields: Record<string, unknown>;
  isActive: boolean;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string | null;
  comments?: { id: number; userId: number; userName: string | null; userPhoto: string | null; comment: string; createdAt: string }[];
  attachments?: { id: number; originalName: string; mimeType: string | null; createdAt: string }[];
}

export const assetCategoriesApi = {
  list: () => api.get<ApiResponse<AssetCategoryDto[]>>('/api/asset-categories'),
  create: (data: { name: string; responsiblePersonIds?: number[] | null }) =>
    api.post<ApiResponse<{ id: number }>>('/api/asset-categories', data),
  update: (id: number, data: { name: string; responsiblePersonIds?: number[] | null }) =>
    api.put<ApiResponse>(`/api/asset-categories/${id}`, data),
  remove: (id: number) => api.delete<ApiResponse>(`/api/asset-categories/${id}`),
};

export interface AssetRequestDto {
  id: number;
  userId: number;
  userName: string | null;
  userPhoto?: string | null;
  categoryId: number | null;
  categoryName: string | null;
  name: string;
  reason: string | null;
  dateCreated: string;
  orgUnit: number | null;
  workflowStatus: 'pending' | 'in_progress' | 'approved' | 'declined';
  workflowLabel: string;
}

export interface AssetRequestAttachmentDto {
  id: number;
  fileName: string;
  dateCreated: string;
}

export interface AssetRequestCommentDto {
  id: number;
  userId: number;
  userName: string | null;
  userPhoto: string | null;
  comment: string;
  createdAt: string;
}

export interface AssetRequestDetailDto extends AssetRequestDto {
  userName: string | null;
  attachments?: AssetRequestAttachmentDto[];
  comments?: AssetRequestCommentDto[];
  tasks: {
    id: number;
    task: string;
    description: string | null;
    toUserId: number;
    toUserName: string | null;
    toUserPhoto: string | null;
    fromUserId: number;
    fromUserName: string | null;
    status: number;
    statusLabel: string;
    active: number;
    dateCreated: string;
    dateDone: string | null;
    reason: string | null;
  }[];
  activeTaskId: number | null;
}

export const assetRequestsApi = {
  list: () => api.get<ApiResponse<AssetRequestDto[]>>('/api/asset-requests'),
  create: (data: { name: string; categoryId: number; reason?: string | null; orgUnit?: number | null }) =>
    api.post<ApiResponse<{ id: number }>>('/api/asset-requests', data),
  get: (id: number) => api.get<ApiResponse<AssetRequestDetailDto>>(`/api/asset-requests/${id}`),
  addComment: (requestId: number, comment: string) =>
    api.post<ApiResponse<{ id: number }>>(`/api/asset-requests/${requestId}/comments`, { comment }),
  uploadAttachment: (requestId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ApiResponse<{ id: number; fileName: string | null }>>(`/api/asset-requests/${requestId}/attachments`, form);
  },
  getAttachments: (requestId: number) =>
    api.get<ApiResponse<AssetRequestAttachmentDto[]>>(`/api/asset-requests/${requestId}/attachments`),
  deleteAttachment: (requestId: number, attachmentId: number) =>
    api.delete<ApiResponse>(`/api/asset-requests/${requestId}/attachments`, { params: { attachmentId } }),
  downloadAttachment: (requestId: number, attachmentId: number) =>
    api.get<Blob>(`/api/asset-requests/${requestId}/attachments/download`, { params: { attachmentId }, responseType: 'blob' }),
};

export interface AssetCustomFieldDefinitionDto {
  id: number;
  categoryId: number | null;
  fieldKey: string;
  label: string;
  fieldType: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string | null;
}

export const assetCustomFieldsApi = {
  list: (params?: { categoryId?: number }) =>
    api.get<ApiResponse<AssetCustomFieldDefinitionDto[]>>('/api/asset-custom-fields', { params }),
  create: (data: { categoryId?: number | null; fieldKey: string; label: string; fieldType?: string; sortOrder?: number }) =>
    api.post<ApiResponse<{ id: number }>>('/api/asset-custom-fields', data),
  update: (id: number, data: { categoryId?: number | null; fieldKey: string; label: string; fieldType?: string; sortOrder?: number }) =>
    api.put<ApiResponse>(`/api/asset-custom-fields/${id}`, data),
  remove: (id: number) => api.delete<ApiResponse>(`/api/asset-custom-fields/${id}`),
};

export const assetsApi = {
  list: (params?: { categoryId?: number; search?: string; isActive?: boolean; ownerUserId?: number; limit?: number; offset?: number; sort_by?: string; sort_order?: string }) =>
    api.get<ApiResponse<AssetDto[]>>('/api/assets', { params }),
  byUsers: () => api.get<ApiResponse<{ users: { id: string; type: string; userId: number; name: string; photo: string | null; assetCount: number; assets: AssetDto[] }[]; labels: { id: string; type: string; label: string; assetCount: number; assets: AssetDto[] }[]; canEdit: boolean }>>('/api/assets/by-users'),
  get: (id: number) => api.get<ApiResponse<AssetDto>>(`/api/assets/${id}`),
  getDetail: (id: number) =>
    api.get<ApiResponse<{ asset: AssetDto; categories: AssetCategoryDto[]; customFieldDefinitions: AssetCustomFieldDefinitionDto[] }>>(`/api/assets/${id}/detail`),
  create: (data: {
    name: string;
    serialNumber?: string | null;
    categoryId?: number | null;
    purchasedAt?: string | null;
    price?: number | null;
    currencyId?: number | null;
    supplier?: string | null;
    ownerUserId?: number | null;
    ownerLabel?: string | null;
    warrantyUntil?: string | null;
    customFields?: Record<string, unknown>;
    isActive?: boolean;
  }) => api.post<ApiResponse<{ id: number }>>('/api/assets', data),
  update: (id: number, data: {
    name: string;
    serialNumber?: string | null;
    categoryId?: number | null;
    purchasedAt?: string | null;
    price?: number | null;
    currencyId?: number | null;
    supplier?: string | null;
    ownerUserId?: number | null;
    ownerLabel?: string | null;
    warrantyUntil?: string | null;
    customFields?: Record<string, unknown>;
    isActive?: boolean;
  }) => api.put<ApiResponse>(`/api/assets/${id}`, data),
  deactivate: (id: number) => api.post<ApiResponse>(`/api/assets/${id}/deactivate`),
  addComment: (id: number, comment: string) => api.post<ApiResponse<{ id: number }>>(`/api/assets/${id}/comments`, { comment }),
  addAttachment: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ApiResponse<{ id: number; originalName: string; createdAt: string }>>(`/api/assets/${id}/attachments`, form);
  },
  downloadAttachment: (attachmentId: number) =>
    api.get<Blob>(`/api/assets/attachments/download?id=${attachmentId}`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId: number) => api.delete<ApiResponse>(`/api/assets/attachments/${attachmentId}`),
  pdfEmployeeCard: (userId: number, language?: string) =>
    api.get<Blob>(`/api/assets/pdf/employee-card/${userId}`, { params: { language: language || 'sk' }, responseType: 'blob' }),
  pdfEmployeeCardByLabel: (label: string, language?: string) =>
    api.get<Blob>('/api/assets/pdf/employee-card-by-label', { params: { label, language: language || 'sk' }, responseType: 'blob' }),
  pdfHandoverProtocol: (params: { userIdFrom: number; userIdTo?: number; labelTo?: string; assetIds: number[]; language?: string; handoverType?: 'receive' | 'return' }) =>
    api.get<Blob>('/api/assets/pdf/handover-protocol', {
      params: {
        userIdFrom: params.userIdFrom,
        userIdTo: params.userIdTo ?? 0,
        labelTo: params.labelTo ?? '',
        assetIds: params.assetIds.length ? params.assetIds.join(',') : '',
        language: params.language || 'sk',
        handoverType: params.handoverType ?? 'receive',
      },
      responseType: 'blob',
    }),
  /** Export filtered assets to CSV. Pass same filters as list. */
  exportCsv: (params?: { categoryId?: number; search?: string; isActive?: boolean; ownerUserId?: number }) =>
    api.get<Blob>('/api/assets/export-csv', { params, responseType: 'blob' }),
  /** Export filtered assets to XLSX (Excel). Pass same filters as list. */
  exportXls: (params?: { categoryId?: number; search?: string; isActive?: boolean; ownerUserId?: number }) =>
    api.get<Blob>('/api/assets/export-xls', { params, responseType: 'blob' }),
  importTemplate: () => api.get<Blob>('/api/assets/import-template', { responseType: 'blob' }),
  importCsv: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ApiResponse<{ imported: number; failed: number; log: { row: number; status: string; message: string }[] }>>('/api/assets/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// E-learning (education) types and API
export interface EducationCategoryDto {
  id: number;
  name: string | Record<string, string>;
}

export interface EducationCourseDto {
  id: number;
  title: string;
  description?: string | null;
  intro?: string | null;
  validDays?: number | null;
  newEmployee?: number;
  onlyOnce?: number;
  daysToComplete?: number | null;
  pdfFile?: string | null;
  minResult?: number;
  timeLimitMinutes?: number | null;
  authorId?: number | null;
  authorName?: string | null;
  categoryId?: number | null;
  categoryName?: string | Record<string, string> | null;
  hide?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  isAssigned?: boolean;
  materialReadAt?: string | null;
  latestResult?: { id: number; passed: boolean; score: string; date: string } | null;
  /** Number of questions in the course. Assign not allowed until > 0. */
  questionCount?: number;
}

export interface EducationQuestionDto {
  id: number;
  courseId: number;
  question: string;
  image?: string | null;
  answer1: string;
  answer2: string;
  answer3: string;
  correct: number; // 1, 2, or 3
}

export interface EducationAssignedUserLatestResultDto {
  resultId: number;
  score: string;
  scorePct: number;
  passed: boolean;
  date: string | null;
}

export interface EducationAssignedUserDto {
  id: number;
  courseId: number;
  userId: number;
  userName?: string | null;
  status: number; // 1 = assigned, 2 = passed
  latestResult?: EducationAssignedUserLatestResultDto | null;
}

export interface EducationResultDetailAnswerDto {
  question: string | null;
  isCorrect: boolean;
  correctAnswer: string | null;
}

export interface EducationResultDetailDto {
  resultId: number;
  score: string;
  passed: boolean;
  date: string | null;
  answers: EducationResultDetailAnswerDto[];
}

export interface EducationMyCourseDto {
  id: number;
  title: string;
  description?: string | null;
  intro?: string | null;
  validDays?: number | null;
  minResult?: number;
  authorName?: string | null;
  categoryId?: number | null;
  categoryName?: string | Record<string, string> | null;
  status: number; // 1 = assigned, 2 = passed
  latestResult?: { id: number; passed: boolean; score: string; date: string } | null;
}

export interface EducationCourseStatsDto {
  totalAssigned: number;
  passed: number;
  failed: number;
  notStarted: number;
  averageScorePct: number | null;
}

export const educationApi = {
  listCategories: () =>
    api.get<ApiResponse<EducationCategoryDto[]>>('/api/education/categories'),
  createCategory: (data: { name: string | Record<string, string> }) =>
    api.post<ApiResponse<{ id: number }>>('/api/education/categories', data),
  updateCategory: (id: number, data: { name: string | Record<string, string> }) =>
    api.put<ApiResponse>(`/api/education/categories/${id}`, data),
  deleteCategory: (id: number) =>
    api.delete<ApiResponse>(`/api/education/categories/${id}`),
  listCourses: (params?: { includeHidden?: boolean; search?: string; categoryId?: number }) =>
    api.get<ApiResponse<EducationCourseDto[]>>('/api/education/courses', { params }),
  getMyCourses: (params?: { search?: string; status?: string }) =>
    api.get<ApiResponse<EducationMyCourseDto[]>>('/api/education/my-courses', { params }),
  getCourse: (id: number, params?: { includeHidden?: boolean }) =>
    api.get<ApiResponse<EducationCourseDto>>(`/api/education/courses/${id}`, { params }),
  getMyAttempts: (courseId: number) =>
    api.get<ApiResponse<{ resultId: number; score: string; passed: boolean; date: string | null }[]>>(`/api/education/courses/${courseId}/my-attempts`),
  getCourseStats: (courseId: number) =>
    api.get<ApiResponse<EducationCourseStatsDto>>(`/api/education/courses/${courseId}/stats`),
  markMaterialRead: (courseId: number) =>
    api.post<ApiResponse<{ materialReadAt: string | null }>>(`/api/education/courses/${courseId}/mark-material-read`),
  createCourse: (data: Partial<EducationCourseDto> & { title: string }) =>
    api.post<ApiResponse<{ id: number }>>('/api/education/courses', data),
  updateCourse: (id: number, data: Partial<EducationCourseDto>) =>
    api.put<ApiResponse>(`/api/education/courses/${id}`, data),
  deleteCourse: (id: number) =>
    api.delete<ApiResponse>(`/api/education/courses/${id}`),
  getQuestions: (courseId: number) =>
    api.get<ApiResponse<EducationQuestionDto[]>>(`/api/education/courses/${courseId}/questions`),
  createQuestion: (courseId: number, data: { question: string; answer1: string; answer2: string; answer3: string; correct: number; image?: string }) =>
    api.post<ApiResponse<{ id: number }>>(`/api/education/courses/${courseId}/questions`, data),
  updateQuestion: (courseId: number, questionId: number, data: Partial<EducationQuestionDto>) =>
    api.put<ApiResponse>(`/api/education/courses/${courseId}/questions/${questionId}`, data),
  deleteQuestion: (courseId: number, questionId: number) =>
    api.delete<ApiResponse>(`/api/education/courses/${courseId}/questions/${questionId}`),
  getAssignedUsers: (courseId: number) =>
    api.get<ApiResponse<EducationAssignedUserDto[]>>(`/api/education/courses/${courseId}/assigned-users`),
  assignUsers: (courseId: number, userIds: number[], replace?: boolean) =>
    api.post<ApiResponse>(`/api/education/courses/${courseId}/assign-users`, { userIds, replace }),
  removeUser: (courseId: number, userId: number) =>
    api.delete<ApiResponse>(`/api/education/courses/${courseId}/users/${userId}`),
  uploadCoursePdf: (courseId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ApiResponse<{ path: string; filename?: string }>>(`/api/education/courses/${courseId}/pdf`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getCoursePdfBlob: (courseId: number) =>
    api.get<Blob>(`/api/education/courses/${courseId}/pdf`, { responseType: 'blob' }),
  submitTest: (courseId: number, answers: Record<number, number>, startedAt?: string) =>
    api.post<ApiResponse<{
      passed: boolean;
      score: string;
      resultId: number;
      details?: { questionId: number; question: string; userChoice: number; correctChoice: number; userAnswerText: string; correctAnswerText: string; isCorrect: boolean }[];
    }>>(`/api/education/courses/${courseId}/submit`, { answers, startedAt: startedAt ?? undefined }),
  certificatePdf: (resultId: number, language?: string) =>
    api.get<Blob>(`/api/education/certificate/${resultId}/pdf`, { params: { lang: language || 'sk' }, responseType: 'blob' }),
  getResultDetail: (resultId: number) =>
    api.get<ApiResponse<EducationResultDetailDto>>(`/api/education/results/${resultId}/detail`),
  courseCertificatePdf: (courseId: number, resultId: number, language?: string) =>
    api.get<Blob>(`/api/education/courses/${courseId}/certificate/${resultId}/pdf`, { params: { lang: language || 'sk' }, responseType: 'blob' }),
};

// Master Login Response type
export interface MasterLoginResponse {
  success: boolean;
  error?: string;
  message?: string;
  require2FA?: boolean;
  userId?: number;
  requireTenantSelection?: boolean;
  needsTenantCreation?: boolean;
  tenants?: TenantInfo[];
  tenant?: TenantInfo;
  user?: {
    id: number;
    email: string;
    userName?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    FirstName?: string;
    LastName?: string;
    language?: string;  // User's preferred language (sk, cs, en)
  };
  settings?: {
    Title?: string;
    [key: string]: any;
  };
}

// Master Auth API (for multi-tenant login)
export const masterAuthApi = {
  login: (email: string, password: string) =>
    api.post<MasterLoginResponse>('/api/master/login', { email, password }),
    
  getTenants: (email: string) =>
    api.get<ApiResponse>('/api/master/tenants', { params: { email } }),
    
  switchTenant: (userId: number, tenantCode: string) =>
    api.post<ApiResponse>('/api/master/switch-tenant', {
      user_id: userId,
      tenant_code: tenantCode
    }),
};

// Tenant API
export const tenantApi = {
  getByCode: (code: string) =>
    api.get<ApiResponse>(`/api/tenants/code/${code}`),
    
  getByDomain: (domain: string) =>
    api.get<ApiResponse>('/api/tenants/domain', { params: { domain } }),
    
  getSettings: (code: string) =>
    api.get<ApiResponse>(`/api/tenants/${code}/settings`),
  
  updateSettings: (code: string, settings: Record<string, string>) =>
    api.put<ApiResponse>(`/api/tenants/${code}/settings`, { settings }),

  updateCurrentSettings: (settings: Record<string, string>) =>
    api.put<ApiResponse>('/api/tenants/settings', { settings }),

  sendTestEmail: (to: string) =>
    api.post<ApiResponse>('/api/tenants/settings/send-test-email', { to }),
    
  // Licenses from master DB
  getLicenses: () =>
    api.get<ApiResponse>('/api/tenant/licenses'),
    
  // Tenant info
  getInfo: () =>
    api.get<ApiResponse>('/api/tenant/info'),
};

// Links API (sidebar custom links)
export const linksApi = {
  list: () =>
    api.get<ApiResponse<{ id: number; name: string; url: string; order: number }[]>>('/api/links'),
  get: (id: number) =>
    api.get<ApiResponse>(`/api/links/${id}`),
  create: (data: { name: string; url: string; order?: number }) =>
    api.post<ApiResponse>('/api/links', data),
  update: (id: number, data: { name?: string; url?: string; order?: number }) =>
    api.put<ApiResponse>(`/api/links/${id}`, data),
  delete: (id: number) =>
    api.delete<ApiResponse>(`/api/links/${id}`),
};

// Organization structure (persons, assignments, tree, my-boss, subordinates)
export const organizationApi = {
  // Get user's organization permission level
  getPermission: () =>
    api.get<ApiResponse<{ canEdit: boolean; level: number | null }>>('/api/org/permission'),
  getTree: (params?: { ou_id?: number; account_unit_id?: number | 'default' | null }) =>
    api.get<ApiResponse<import('@/types/organization').OrgUnitNode[]>>('/api/org/tree', { params }),
  getPersons: (params?: import('@/types/organization').PersonsListParams) =>
    api.get<ApiResponse<import('@/types/organization').PersonsListResponse>>('/api/org/persons', { params }),
  getPerson: (id: number) =>
    api.get<ApiResponse<{ person: import('@/types/organization').OrganizationPerson & { manager_name?: string | null; team_leader_name?: string | null }; assignments: import('@/types/organization').OrganizationAssignment[] }>>(`/api/org/persons/${id}`),
  /** Get person by master user ID (master.users.Id). Returns person + manager_name, team_leader_name + assignments. */
  getPersonByUserId: (userId: number) =>
    api.get<ApiResponse<{ person: import('@/types/organization').OrganizationPerson & { manager_name?: string | null; team_leader_name?: string | null }; assignments: import('@/types/organization').OrganizationAssignment[] }>>(`/api/org/persons/by-user/${userId}`),
  getPersonByTenantUser: (tenantUserId: number) =>
    api.get<ApiResponse<{ person: import('@/types/organization').OrganizationPerson; assignments: import('@/types/organization').OrganizationAssignment[] }>>(`/api/org/persons/by-tenant-user/${tenantUserId}`),
  createPerson: (data: { type: 'internal' | 'external'; user_id?: number; external_name?: string; external_last_name?: string; external_email?: string; external_phone?: string; external_note?: string; active?: boolean }) =>
    api.post<ApiResponse<import('@/types/organization').OrganizationPerson>>('/api/org/persons', data),
  updatePerson: (id: number, data: Partial<{ user_id: number; external_name: string; external_last_name: string; external_email: string; external_phone: string; external_note: string; active: boolean }>) =>
    api.put<ApiResponse<import('@/types/organization').OrganizationPerson>>(`/api/org/persons/${id}`, data),
  deletePerson: (id: number) =>
    api.delete<ApiResponse>(`/api/org/persons/${id}`),
  getMyBoss: () =>
    api.get<ApiResponse<import('@/types/organization').OrganizationPerson | null>>('/api/org/my-boss'),
  getMyHeadBoss: () =>
    api.get<ApiResponse<import('@/types/organization').OrganizationPerson | null>>('/api/org/my-head-boss'),
  getSubordinates: () =>
    api.get<ApiResponse<import('@/types/organization').OrganizationPerson[]>>('/api/org/subordinates'),
  getOrgUnits: (params?: { tree?: boolean; account_unit_id?: number | 'default' | null }) =>
    api.get<ApiResponse<import('@/types/organization').OrgUnitFlat[] | import('@/types/organization').OrgUnitNode[]>>('/api/org/org-units', { params }),
  getOrgUnit: (id: number) =>
    api.get<ApiResponse<import('@/types/organization').OrgUnitDetail>>(`/api/org/org-units/${id}`),
  getOrgUnitAssignments: (ouId: number) =>
    api.get<
      ApiResponse<
        Array<{
          id: number;
          organization_person_id: number;
          position_id: number;
          sort_order: number;
          position_name: string;
          person: import('@/types/organization').OrganizationPerson;
        }>
      >
    >(`/api/org/org-units/${ouId}/assignments`),
  createOrgUnit: (data: import('@/types/organization').OrgUnitCreateUpdate) =>
    api.post<ApiResponse<import('@/types/organization').OrgUnitFlat>>('/api/org/org-units', data),
  updateOrgUnit: (id: number, data: Partial<import('@/types/organization').OrgUnitCreateUpdate>) =>
    api.put<ApiResponse<import('@/types/organization').OrgUnitFlat>>(`/api/org/org-units/${id}`, data),
  deleteOrgUnit: (id: number) =>
    api.delete<ApiResponse>(`/api/org/org-units/${id}`),
  getPositions: () =>
    api.get<ApiResponse<import('@/types/organization').PositionOption[]>>('/api/org/positions'),
  getPosition: (id: number) =>
    api.get<ApiResponse<import('@/types/organization').PositionOption>>(`/api/org/positions/${id}`),
  createPosition: (data: { name: import('@/types/organization').TranslatableName }) =>
    api.post<ApiResponse<import('@/types/organization').PositionOption>>('/api/org/positions', data),
  updatePosition: (id: number, data: { name: import('@/types/organization').TranslatableName }) =>
    api.put<ApiResponse<import('@/types/organization').PositionOption>>(`/api/org/positions/${id}`, data),
  deletePosition: (id: number) =>
    api.delete<ApiResponse>(`/api/org/positions/${id}`),
  exportPositionsCsv: () =>
    api.get<Blob>('/api/org/positions/export-csv', { responseType: 'blob' }),
  importPositionsTemplate: () =>
    api.get<Blob>('/api/org/positions/import-template', { responseType: 'blob' }),
  importPositionsCsv: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ApiResponse<{ imported: number; failed: number; log: { row: number; status: string; message: string }[] }>>(
      '/api/org/positions/import',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  getBossPeriods: (ouId: number) =>
    api.get<
      ApiResponse<
        Array<{
          id: number;
          organization_person_id: number;
          valid_from: string | null;
          valid_to: string | null;
          person: import('@/types/organization').OrganizationPerson;
        }>
      >
    >(`/api/org/org-units/${ouId}/boss-periods`),
  setOuBoss: (ouId: number, bossPersonId: number | null, validFrom?: string | null, validTo?: string | null) =>
    api.put<ApiResponse>(`/api/org/org-units/${ouId}/boss`, {
      boss_person_id: bossPersonId,
      ...(validFrom != null && { valid_from: validFrom }),
      ...(validTo != null && { valid_to: validTo }),
    }),
  createAssignment: (data: { organization_person_id: number; organizational_unit_id: number; position_id: number; valid_from?: string; valid_to?: string; sort_order?: number }) =>
    api.post<ApiResponse<{ id: number }>>('/api/org/assignments', data),
  updateAssignment: (id: number, data: Partial<{ organization_person_id: number; organizational_unit_id: number; position_id: number; valid_from: string; valid_to: string; sort_order: number }>) =>
    api.put<ApiResponse>(`/api/org/assignments/${id}`, data),
  deleteAssignment: (id: number) =>
    api.delete<ApiResponse>(`/api/org/assignments/${id}`),
};

// Restaurant cities (categories for dashboard restaurants widget)
export interface RestaurantCityDto {
  id: number;
  name: string;
  order: number;
}

// Restaurants (with city info for dashboard)
export interface RestaurantDto {
  id: number;
  restaurant_city_id: number;
  name: string;
  cuisine_type: string | null;
  rating: number;
  avatar: string | null;
  url: string | null;
  order: number;
  city_name?: string;
  city_order?: number;
}

export const restaurantCitiesApi = {
  list: () =>
    api.get<ApiResponse<RestaurantCityDto[]>>('/api/restaurant-cities'),
};

export const restaurantsApi = {
  list: () =>
    api.get<ApiResponse<RestaurantDto[]>>('/api/restaurants'),
  listByCity: (cityId: number) =>
    api.get<ApiResponse<RestaurantDto[]>>('/api/restaurants', { params: { city_id: cityId } }),
};

// Pages API (CMS-like pages with sections and comments)
export interface PageAuthorDto {
  id: number;
  firstName?: string;
  lastName?: string;
  userName?: string;
  photo?: string | null;
}

export interface PageSectionDto {
  id: number;
  sortOrder: number;
  type: string;
  title: Record<string, string>;
  content: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
}

export interface PageDto {
  id: number;
  title: Record<string, string>;
  slug: string;
  content?: Record<string, string>;
  status: number;
  sections?: PageSectionDto[];
  sectionsCount?: number;
  commentsCount?: number;
  createdAt: string;
  updatedAt?: string;
  createdBy?: PageAuthorDto | null;
  updatedBy?: PageAuthorDto | null;
}

export interface PageCommentDto {
  id: number;
  pageId: number;
  userId: number;
  comment: string;
  createDate: string;
  author?: PageAuthorDto | null;
}

export interface PageSidebarDto {
  id: number;
  title: Record<string, string>;
  slug: string;
}

export const pagesApi = {
  // List pages with pagination
  list: (params?: { limit?: number; offset?: number; status?: 'active' | 'inactive' }) =>
    api.get<ApiResponse<PageDto[]> & { pagination?: { total: number; limit: number; offset: number } }>('/api/pages', { params }),
  
  // Get pages for sidebar (active only, minimal data)
  sidebar: () =>
    api.get<ApiResponse<PageSidebarDto[]>>('/api/pages/sidebar'),
  
  // Get page by ID
  get: (id: number) =>
    api.get<ApiResponse<PageDto>>(`/api/pages/${id}`),
  
  // Get page by slug (for public viewing)
  getBySlug: (slug: string) =>
    api.get<ApiResponse<PageDto>>(`/api/pages/slug/${slug}`),
  
  // Create page
  create: (data: { title: Record<string, string>; content?: Record<string, string>; slug?: string; status?: number }) =>
    api.post<ApiResponse<{ id: number; slug: string }>>('/api/pages', data),
  
  // Update page
  update: (id: number, data: { title: Record<string, string>; content?: Record<string, string>; slug?: string; status?: number }) =>
    api.put<ApiResponse>(`/api/pages/${id}`, data),
  
  // Delete page (soft delete)
  delete: (id: number) =>
    api.delete<ApiResponse>(`/api/pages/${id}`),
  
  // Sections
  addSection: (pageId: number, data: { type?: string; title?: Record<string, string>; content?: Record<string, string>; sortOrder?: number }) =>
    api.post<ApiResponse<{ id: number }>>(`/api/pages/${pageId}/sections`, data),
  
  updateSection: (pageId: number, sectionId: number, data: { type?: string; title?: Record<string, string>; content?: Record<string, string>; sortOrder?: number }) =>
    api.put<ApiResponse>(`/api/pages/${pageId}/sections/${sectionId}`, data),
  
  deleteSection: (pageId: number, sectionId: number) =>
    api.delete<ApiResponse>(`/api/pages/${pageId}/sections/${sectionId}`),
  
  reorderSections: (pageId: number, sections: { id: number; sortOrder: number }[]) =>
    api.put<ApiResponse>(`/api/pages/${pageId}/sections/reorder`, { sections }),
  
  // Comments
  listComments: (pageId: number) =>
    api.get<ApiResponse<PageCommentDto[]>>(`/api/pages/${pageId}/comments`),
  
  addComment: (pageId: number, comment: string) =>
    api.post<ApiResponse<{ id: number }>>(`/api/pages/${pageId}/comments`, { comment }),
  
  deleteComment: (pageId: number, commentId: number) =>
    api.delete<ApiResponse>(`/api/pages/${pageId}/comments/${commentId}`),
};

// Modules API
export interface AppModule {
  id: string;
  name: string;
  key: string;
  icon: string;
  description?: string;
  submenus?: string[];
  always_available?: boolean;
}

export const modulesApi = {
  list: () =>
    api.get<ApiResponse<AppModule[]>>('/api/modules'),
    
  get: (key: string) =>
    api.get<ApiResponse<AppModule>>(`/api/modules/${key}`),
    
  getSubmenus: (key: string) =>
    api.get<ApiResponse<string[]>>(`/api/modules/${key}/submenus`),
};

// Personal dashboard layout (see components/dashboard/DashboardGrid.tsx)
export interface DashboardLayoutBox {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface SavedDashboardLayout {
  version: number;
  widgets: string[];
  layouts: Partial<Record<'xl' | 'lg' | 'md' | 'sm', DashboardLayoutBox[]>>;
}

export const dashboardLayoutApi = {
  get: () =>
    api.get<ApiResponse<{ layout: SavedDashboardLayout | null }>>('/api/dashboard/layout'),

  save: (layout: SavedDashboardLayout) =>
    api.put<ApiResponse<{ layout: SavedDashboardLayout }>>('/api/dashboard/layout', { layout }),

  reset: () =>
    api.delete<ApiResponse<{ layout: null }>>('/api/dashboard/layout'),
};

// Types for multi-tenant
export interface TenantInfo {
  id: number;
  code: string;
  name: string;
  database?: string;
  role?: string;
  is_primary?: boolean;
  last_accessed_at?: string;
}

export interface MasterUser {
  id: number;
  email: string;
  name: string;
  avatar?: string;
  locale?: string;
  timezone?: string;
  default_tenant_id?: number;
}

// Export default instance
export default api;
